import { normalize_text } from '@/services/onboarding_data_helpers';

const count_by = (cases, predicate) => cases.filter(predicate).length;
const first_value = (values, fallback = '') => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return value === undefined ? fallback : value;
};
const get_status_label = (case_item) => {
  const raw_status = first_value([
    case_item?.current_workflow_status,
    case_item?.workflow_status_name,
    case_item?.stage,
    case_item?.application_status,
    case_item?.status?.status,
    case_item?.status?.name,
    case_item?.status_name,
    case_item?.status
  ], '');

  if (raw_status && typeof raw_status === 'object') {
    return first_value([
      raw_status?.status,
      raw_status?.name,
      raw_status?.label
    ], '');
  }

  return String(raw_status || '').trim();
};
const has_document_content = (document) => Boolean(first_value([
  document?.question,
  document?.file_name,
  document?.name,
  document?.document_name,
  document?.answer?.file_name,
  document?.answer?.presigned_url,
  document?.answer?.file_url,
  document?.presigned_url,
  document?.file_url,
  document?.url,
  document?.document_url
], ''));

const build_document_case_summary = (case_item) => {
  const documents = Array.isArray(case_item?.documents) ? case_item.documents.filter(Boolean) : [];
  const forms = Array.isArray(case_item?.forms) ? case_item.forms.filter(Boolean) : [];
  const display_documents = documents.filter(has_document_content);

  if (display_documents.length) {
    const unresolved_documents = display_documents.filter((document) => !document?.is_verified);
    return {
      has_document_work: unresolved_documents.length > 0,
      unresolved_count: unresolved_documents.length
    };
  }

  return {
    has_document_work: forms.length > 0,
    unresolved_count: forms.length
  };
};

function get_sla_runtimes(case_item = {}) {
  const runtimes = case_item?.onboarding_status?.sla?.runtimes;
  return Array.isArray(runtimes) ? runtimes : [];
}

function get_primary_sla_runtime(case_item = {}) {
  const runtimes = get_sla_runtimes(case_item);
  const open_runtime = runtimes.find((runtime) => !['closed'].includes(String(runtime?.status || '').toLowerCase()));
  return open_runtime || runtimes[0] || null;
}

function format_sla_date(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

const OVERDUE_SLA_STATUSES = new Set(['breached', 'escalated']);

function get_sla_due_prefix(status = '') {
  return OVERDUE_SLA_STATUSES.has(status) ? 'Overdue' : 'Due';
}

function get_sla_fallback_label(status = '', status_label = '') {
  return OVERDUE_SLA_STATUSES.has(status) ? 'Overdue' : status_label;
}

function build_runtime_sla_due_label(runtime = {}) {
  const status = String(runtime?.status || '').toLowerCase();
  const due_date = format_sla_date(runtime?.due_at);
  const status_label = first_value([runtime?.status_label, status], 'active');
  const prefix = get_sla_due_prefix(status);
  return due_date ? `${prefix} ${due_date}` : get_sla_fallback_label(status, status_label);
}

function build_sla_due_label(case_item = {}) {
  const runtime = get_primary_sla_runtime(case_item);
  return runtime ? build_runtime_sla_due_label(runtime) : first_value([case_item.eta], '-');
}

const count_document_cases = (cases) => (
  cases.filter((case_item) => build_document_case_summary(case_item).has_document_work).length
);

const count_unresolved_documents = (cases) => (
  cases.reduce((total, case_item) => total + build_document_case_summary(case_item).unresolved_count, 0)
);

function get_case_journey_value(case_item) {
  const journey_value = first_value([
    case_item?.journey_key,
    case_item?.journey_tone,
    case_item?.journey
  ], '');

  return journey_value;
}

function get_journey_count(cases) {
  const journey_values = cases
    .map(get_case_journey_value)
    .filter(Boolean);
  const unique_journeys = new Set(journey_values);

  return unique_journeys.size;
}

function get_scope_detail(cases, scope_label) {
  if (scope_label && scope_label !== 'All Journeys') return scope_label;

  const journey_count = get_journey_count(cases);

  return `${journey_count} ${journey_count === 1 ? 'journey' : 'journeys'}`;
}

export const build_dashboard_stats = (cases, scope_label = '') => {
  const blocked_count = count_by(cases, (case_item) => case_item.activation_tone === 'blocked');
  const ready_count = count_by(cases, (case_item) => case_item.activation_tone === 'ready');
  const pending_count = count_by(cases, (case_item) => case_item.activation_tone === 'pending');
  const high_risk_count = count_by(cases, (case_item) => case_item.risk_tone === 'high');
  const document_blocker_count = count_document_cases(cases);
  const unresolved_document_count = count_unresolved_documents(cases);

  return [
    { label: 'Active onboardings', value: String(cases.length), detail: get_scope_detail(cases, scope_label), tone: 'primary', bars: [4, 7, 8, 11, 13, 18] },
    { label: 'Pending approvals', value: String(pending_count), detail: 'Awaiting action', tone: 'warning', bars: [5, 6, 8, 11, 14, 19] },
    { label: 'Missing documents', value: String(unresolved_document_count), detail: `${document_blocker_count} onboardings need document action`, tone: 'danger', bars: [5, 7, 8, 11, 13, 16] },
    { label: 'High risk', value: String(high_risk_count), detail: 'Review required', tone: 'danger', badge: String(high_risk_count) },
    { label: 'Ready to activate', value: String(ready_count), detail: 'Activation-ready', tone: 'success', badge: String(ready_count) }
  ].map((stat) => Object.assign({}, stat, { blocked_count }));
};

export const build_funnel_steps = (cases) => {
  const total = cases.length;
  const blocked_count = count_by(cases, (case_item) => case_item.activation_tone === 'blocked');
  const ready_count = count_by(cases, (case_item) => case_item.activation_tone === 'ready');
  const pending_count = count_by(cases, (case_item) => case_item.activation_tone === 'pending');

  return [
    { step: '1', label: 'Request', value: String(total), detail: 'All inflow', tone: 'primary' },
    { step: '2', label: 'Profile', value: String(total), detail: 'Profiles created', tone: 'primary' },
    { step: '3', label: 'Documents', value: String(count_document_cases(cases)), detail: `${count_unresolved_documents(cases)} items outstanding`, tone: 'danger' },
    { step: '4', label: 'Verification', value: String(count_by(cases, (case_item) => normalize_text(case_item.stage).includes('verification'))), detail: 'Verification stage', tone: 'warning' },
    { step: '5', label: 'Risk review', value: String(count_by(cases, (case_item) => case_item.risk_tone === 'high')), detail: 'High risk', tone: 'danger' },
    { step: '6', label: 'Approval', value: String(pending_count), detail: 'Pending', tone: 'warning' },
    { step: '7', label: 'Blocked', value: String(blocked_count), detail: 'Needs action', tone: 'danger' },
    { step: '8', label: 'Activation', value: String(ready_count), detail: 'Ready', tone: 'success' }
  ];
};

export const build_queue_items = (cases) => cases.map((case_item) => Object.assign({}, {
  id: case_item.id,
  case_item,
  title: `${case_item.name} · ${get_status_label(case_item) || case_item.detail || case_item.stage}`,
  company: first_value([
    case_item.onboarding_project_name,
    case_item.profile?.project?.name,
    case_item.profile?.project_name,
    case_item.project?.name,
    case_item.project_name
  ], '-'),
  journey: case_item.journey,
  gate: get_status_label(case_item) || case_item.stage,
  progress: `${case_item.progress}% complete`,
  priority: first_value([
    case_item.risk,
    case_item.risk_rating,
    case_item.risk_level
  ], 'Unknown'),
  due: build_sla_due_label(case_item),
  status: case_item.activation_tone === 'ready' ? 'success' : case_item.activation_tone === 'blocked' ? 'danger' : 'warning'
}));
